import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Lock, ShieldCheck, Printer, Globe, Wand2, Sparkles,
  LayoutDashboard, Languages, Zap, MessageSquareText, Building2,
  ScrollText, FileSearch, PaintBucket, Repeat, Database,
  ArrowRight, CheckCircle2, Cpu, Receipt, MapPin, Star,
  ToggleLeft, Phone, CreditCard, Clock, Mic, SlidersHorizontal,
  Menu, X, CalendarDays, UserCheck, Brain
} from "lucide-react";
import RevealNav from "@/components/RevealNav";
import SiteFooter from "@/components/SiteFooter";
import StoryScroll from "@/components/ui/story-scroll";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const fade = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };

const TOC_ITEMS = [
  { id: "s01", label: "AI Storefront Generator" },
  { id: "s02", label: "AI Phone Host" },
  { id: "s03", label: "Merchant Portal" },
  { id: "s04", label: "Provider Portal" },
  { id: "s05", label: "Loyalty & Engagement" },
  { id: "s06", label: "Direct Commerce" },
  { id: "s07", label: "Fulfillment & Scheduling" },
  { id: "s08", label: "Enterprise Compliance" },
  { id: "s09", label: "Immediate Deployment" },
  { id: "s10", label: "AI Business Advisor" },
];

function TableOfContents() {
  const [active, setActive] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Story-scroll pinning leaves sections translated by a viewport after their pin
  // range, so live rects (IntersectionObserver, scrollIntoView on the section) point
  // one panel off. GSAP's un-transformed .pin-spacer wrapper keeps the true position.
  const anchorFor = (id) => {
    const el = document.getElementById(id);
    return el ? el.closest(".pin-spacer") || el : null;
  };

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const probe = window.scrollY + window.innerHeight * 0.4;
        let current = null;
        TOC_ITEMS.forEach(({ id }) => {
          const anchor = anchorFor(id);
          if (!anchor) return;
          const top = anchor.getBoundingClientRect().top + window.scrollY;
          if (top <= probe) current = id;
        });
        setActive(current);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  const scrollTo = (id) => {
    anchorFor(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileOpen(false);
  };

  return (
    <>
      {/* Desktop: sticky bottom-left rail */}
      <nav className="hidden xl:block fixed bottom-6 left-6 z-30 w-52">
        <div className="bg-background/90 backdrop-blur-sm border border-border rounded-xl p-4 shadow-sm">
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-3 pl-1">On this page</p>
          <ul className="space-y-0.5">
            {TOC_ITEMS.map(({ id, label }) => {
              const isActive = active === id;
              return (
                <li key={id}>
                  <button
                    onClick={() => scrollTo(id)}
                    className={`w-full text-left text-xs px-3 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-2 ${
                      isActive
                        ? "bg-primary/10 text-primary font-semibold"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    }`}
                  >
                    {isActive && <span className="shrink-0 w-1 h-1 rounded-full bg-primary" />}
                    {label}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </nav>

      {/* Mobile: floating pill button + slide-up drawer */}
      <div className="xl:hidden fixed bottom-6 right-4 z-40">
        <button
          onClick={() => setMobileOpen((v) => !v)}
          className="flex items-center gap-2 bg-foreground text-background text-xs font-bold px-4 py-2.5 rounded-full shadow-lg"
          aria-label="Toggle table of contents"
        >
          {mobileOpen ? <X className="w-3.5 h-3.5" /> : <Menu className="w-3.5 h-3.5" />}
          {mobileOpen ? "Close" : "Sections"}
        </button>

        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.18 }}
            className="absolute bottom-12 right-0 w-56 bg-background border border-border rounded-xl shadow-xl p-3 mb-2"
          >
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2 pl-1">Jump to</p>
            <ul className="space-y-0.5">
              {TOC_ITEMS.map(({ id, label }) => {
                const isActive = active === id;
                return (
                  <li key={id}>
                    <button
                      onClick={() => scrollTo(id)}
                      className={`w-full text-left text-xs px-3 py-2 rounded-lg transition-all flex items-center gap-2 ${
                        isActive
                          ? "bg-primary/10 text-primary font-semibold"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                      }`}
                    >
                      {isActive && <span className="shrink-0 w-1 h-1 rounded-full bg-primary" />}
                      {label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </motion.div>
        )}
      </div>
    </>
  );
}

const Section = ({ id, idx, kicker, title, scriptTail, lead, children, dark }) => (
  <section id={id} data-story-section className={`solutions-panel solutions-panel--${idx}`}>
    <div className="solutions-panel-inner" data-story-inner>
      <div className="solutions-section-card">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-5">
            <p className="overline mb-4 opacity-70">{idx} · {kicker}</p>
            <h2 className="text-5xl lg:text-6xl xl:text-7xl font-black leading-[0.95] mb-6 tracking-tight">
              {title}
              {scriptTail && (
                <span className="font-script text-primary text-6xl lg:text-7xl font-normal leading-[0.7] block mt-3">
                  {scriptTail}
                </span>
              )}
            </h2>
            <p className="leading-relaxed opacity-70 text-lg">
              {lead}
            </p>
          </div>
          <div className="lg:col-span-7">{children}</div>
        </div>
      </div>
    </div>
  </section>
);

const Feat = ({ icon: Icon, title, children }) => (
  <div className="flex gap-4">
    <div className="shrink-0 w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center">
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <h4 className="font-bold mb-1">{title}</h4>
      <p className="text-sm text-muted-foreground leading-relaxed">{children}</p>
    </div>
  </div>
);

const DarkFeat = ({ icon: Icon, title, children }) => (
  <div className="flex gap-4">
    <div className="shrink-0 w-10 h-10 rounded-lg bg-primary/15 text-primary grid place-items-center">
      <Icon className="w-5 h-5" />
    </div>
    <div>
      <h4 className="font-bold mb-1 text-background">{title}</h4>
      <p className="text-sm text-background/70 leading-relaxed">{children}</p>
    </div>
  </div>
);

export default function Solutions() {
  return (
    <div className="solutions-page">
      <RevealNav />
      <TableOfContents />

      <StoryScroll>
      {/* HERO */}
      <section data-story-section className="solutions-panel solutions-panel--hero relative overflow-hidden">
        <div className="solutions-panel-inner text-center" data-story-inner>
          <motion.div className="solutions-hero-card" {...fade}>
            <p className="overline mb-6">solutions</p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl mb-6">
              Everything you need to run
              <span className="font-script text-primary text-7xl sm:text-8xl lg:text-[7.5rem] font-normal leading-[0.7] block mt-3">
                direct-to-consumer ordrs.
              </span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-8">
              A full-stack platform that generates your storefront from a catalog or service list, takes orders and bookings with a real AI host,
              routes them straight to your queue or calendar, and pays out the second a customer checks out — all on your own domain, with your Stripe.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {[
                "AI storefront generator",
                "Catalog & site parsing",
                "AI phone host",
                "Loyalty program",
                "Bring your own domain",
                "Bring your own Stripe",
                "Merchant & provider portals",
                "Multi-location",
                "Enterprise compliance",
                "Immediate deployment",
              ].map((t) => (
                <Badge key={t} variant="outline" className="text-xs font-medium py-1.5 px-3 border-border">
                  {t}
                </Badge>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* 01 — AI STOREFRONT GENERATOR */}
      <Section
        id="s01" idx="01" kicker="ai storefront generator"
        title="Drop your catalog or services in."
        scriptTail="Get a brand."
        lead="Paste product or service text, upload a PDF or photo, or hand us your existing website. Our AI extracts structured categories, items, and prices — or your service menu and rates. The brand-pack scanner pulls your hero image, primary color, and font family. Style chips and plain-English prompts steer the final look. Iterate as many times as you want before paying anything."
      >
        <Card className="p-8 h-full">
          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <Feat icon={FileSearch} title="Catalog & service parsing">Text, PDF, JPEG, PNG — vision and text models extract categories and prices into a structured schema, for any product or service list.</Feat>
            <Feat icon={Globe} title="Website scrape">Hand us your existing site URL. We crawl, strip, and feed the content to the builder in one pass.</Feat>
            <Feat icon={PaintBucket} title="Brand-pack auto-extract">Hero image, primary color, and Google Fonts family pulled directly from your site's meta tags and stylesheets.</Feat>
            <Feat icon={Wand2} title="Style chips + custom prompt">Pick from 4 archetypes or describe your own aesthetic in plain English ("clean white label with dark green accents").</Feat>
            <Feat icon={Repeat} title="Iterate on preview">Type "switch to luxury_dark and add a new service at $80" — the AI applies the diff to brand and listings live.</Feat>
            <Feat icon={Sparkles} title="Free preview tier">No card, no account required. One free refinement included. $5 unlocks 10 more credits (50¢ per prompt).</Feat>
          </div>
          <div className="bg-foreground text-background rounded-lg p-5 font-mono text-xs space-y-1.5">
            <div className="text-background/50">// /api/instant-preview</div>
            <div>input: <span className="text-primary">"Apothecary supply co, clean white label, dark green"</span></div>
            <div>parsed: 5 categories, 22 items</div>
            <div>brand-pack: hero+color from existing site</div>
            <div className="text-primary">→ live storefront in &lt;5min</div>
          </div>
        </Card>
      </Section>

      {/* 02 — AI PHONE HOST */}
      <Section
        id="s02" idx="02" kicker="ai phone host" dark
        title="A line that never"
        scriptTail="sleeps, slows, or forgets."
        lead="A real phone number powered by Twilio Media Streams. Audio is bridged in real-time to a custom WebSocket on DeepSeek's reasoning architecture. Grounded in your live catalog or service menu, your hours, your details. Speaks 8 languages. Drops a Stripe checkout or booking confirmation link to the caller's SMS the moment they're ready. $0.10 per call, billed monthly."
      >
        <Card className="p-8 h-full bg-zinc-900 border-zinc-800 text-background">
          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <DarkFeat icon={Mic} title="Twilio Media Streams">Real phone numbers, real carrier networks, low-latency raw audio piped to our WebSocket bridge.</DarkFeat>
            <DarkFeat icon={Cpu} title="DeepSeek architecture">No expensive OpenAI Realtime rates. Custom bridge tuned for sub-800ms turnaround at $0.10/call.</DarkFeat>
            <DarkFeat icon={Languages} title="Multilingual host">8 languages out of the box. Caller speaks their language, your queue or calendar gets clean English records.</DarkFeat>
            <DarkFeat icon={ScrollText} title="Grounded in your business">Live catalog or service list and real-time availability feed the system prompt. Sold-out items and booked slots are never offered.</DarkFeat>
            <DarkFeat icon={MessageSquareText} title="SMS confirmation link">Phone orders and bookings enter a draft state. Caller gets a secure pay or confirmation link via SMS. It only finalizes once Stripe confirms.</DarkFeat>
            <DarkFeat icon={ShieldCheck} title="Optional AI disclosure">One-toggle AI and recording disclosure, played at the start of every call once enabled — some states (CA, FL, IL, and others) require it; you control whether and how it's worded.</DarkFeat>
          </div>
          <div className="bg-black/40 border border-zinc-800 rounded-lg p-5 font-mono text-xs space-y-1.5">
            <div className="text-background/50"># inbound call → wss://your.app/api/twilio/media-stream</div>
            <div className="text-background/90">caller (ES): "¿Tienen algo sin gluten?"</div>
            <div className="text-primary">agent: "Sí — tenemos la opción de coliflor ($18) y la de salmón ($32)."</div>
            <div className="text-background/50"># 7 ms STT · 412 ms deepseek · 89 ms TTS</div>
          </div>
        </Card>
      </Section>

      {/* 03 — MERCHANT PORTAL */}
      <Section
        id="s03" idx="03" kicker="merchant portal"
        title="A back-office that"
        scriptTail="actually runs the business."
        lead="One dashboard. Every tab you need. Live order queue, fulfillment display, catalog manager with availability toggles and optimistic locking, customer CRM with lifetime value, multi-location config, call transcripts, loyalty, and full checkout settings — all in one place."
      >
        <Card className="p-8 h-full">
          <div className="grid sm:grid-cols-2 gap-6">
            <Feat icon={ScrollText} title="Order queue">Every order with source flag (Web vs Phone Call), payment and fulfillment status, customer info, and total.</Feat>
            <Feat icon={CheckCircle2} title="Fulfillment display (KDS)">Auto-refreshing 4-column pipeline: Pending → Preparing → Ready → Completed. Audible chime on new arrivals. Toggle off if your business has no prep step.</Feat>
            <Feat icon={LayoutDashboard} title="Catalog manager">Full CRUD with version-based optimistic locking. Per-item image, description, price, and availability switch.</Feat>
            <Feat icon={Receipt} title="Customer CRM">Aggregated by phone number. Lifetime value, order count, last order date. Your list — owned by you.</Feat>
            <Feat icon={MapPin} title="Multi-location">Each location has its own address, phone, hours, tax rate, and printer config. Enterprise plan ($99/location/mo) for 2+ locations.</Feat>
            <Feat icon={MessageSquareText} title="Call transcripts">Every AI phone interaction logged with full transcript. 365-day retention by default. One-click deletion for CCPA/CPRA compliance.</Feat>
          </div>
        </Card>
      </Section>

      {/* 04 — PROVIDER PORTAL */}
      <Section
        id="s04" idx="04" kicker="provider portal" dark
        title="A calendar that"
        scriptTail="actually runs the business."
        lead="Same dashboard, built for appointments instead of tickets. Live booking calendar, staff scheduling, service list manager with duration and pricing, customer CRM with lifetime value, multi-location config, call transcripts, and full checkout settings — all in one place."
      >
        <Card className="p-8 h-full bg-zinc-900 border-zinc-800 text-background">
          <div className="grid sm:grid-cols-2 gap-6">
            <DarkFeat icon={CalendarDays} title="Booking calendar">Every appointment with source flag (Web vs Phone Call), payment and confirmation status, customer info, and duration.</DarkFeat>
            <DarkFeat icon={UserCheck} title="Staff scheduling">Assign appointments to specific staff or resources. Real-time availability feeds directly into the AI phone host.</DarkFeat>
            <DarkFeat icon={LayoutDashboard} title="Service list manager">Full CRUD with version-based optimistic locking. Per-service description, price, duration, and availability switch.</DarkFeat>
            <DarkFeat icon={Receipt} title="Customer CRM">Aggregated by phone number. Lifetime value, visit count, last appointment date. Your list — owned by you.</DarkFeat>
            <DarkFeat icon={MapPin} title="Multi-location">Each location has its own address, phone, hours, and tax rate. Enterprise plan ($99/location/mo) for 2+ locations.</DarkFeat>
            <DarkFeat icon={MessageSquareText} title="Call transcripts">Every AI phone interaction logged with full transcript. 365-day retention by default. One-click deletion for CCPA/CPRA compliance.</DarkFeat>
          </div>
        </Card>
      </Section>

      {/* 06 — LOYALTY & ENGAGEMENT */}
      <Section
        id="s05" idx="05" kicker="loyalty & engagement"
        title="Turn one-time buyers into"
        scriptTail="regulars."
        lead="A built-in points program that runs automatically. Customers earn 1 point per $1 of subtotal on every paid order or appointment, tied to their phone number — no app to download, no separate account to create. Points carry across all your locations. Toggle it on when you're ready; off when you're not."
      >
        <Card className="p-8 h-full">
          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <Feat icon={Star} title="Automatic point accrual">1 point per $1 of subtotal, triggered on every paid order or booking. No staff action required.</Feat>
            <Feat icon={Phone} title="Phone-number identity">Points are tied to the customer's phone number. Works across web and phone orders or bookings with no app or signup.</Feat>
            <Feat icon={CreditCard} title="Simple redemption">100 points = $1 off. Staff apply redemptions from the Loyalty tab. Self-service checkout redemption is on the roadmap.</Feat>
            <Feat icon={Building2} title="Cross-location points">A customer who earns at your downtown location can redeem at your uptown location. Scoped to your merchant account.</Feat>
            <Feat icon={SlidersHorizontal} title="Manual adjustments">Add or deduct points from any customer directly from the Loyalty tab — for promotions, corrections, or goodwill credits.</Feat>
            <Feat icon={ToggleLeft} title="Optional & non-destructive">Disable loyalty at any time from Settings → Feature toggles. Points are preserved and restored if you re-enable later.</Feat>
          </div>
          <div className="bg-foreground text-background rounded-lg p-5 font-mono text-xs space-y-1.5">
            <div className="text-background/50">// customer: +1 (555) 234-5678</div>
            <div>order_subtotal: <span className="text-primary">$47.00</span> → <span className="text-primary">+47 points</span></div>
            <div>lifetime_points: <span className="text-primary">312</span> → redeemable: <span className="text-primary">$3.00 off</span></div>
            <div className="text-background/50">// no app. no account. just their number.</div>
          </div>
        </Card>
      </Section>

      {/* 06 — DIRECT COMMERCE */}
      <Section
        id="s06" idx="06" kicker="direct commerce" dark
        title="Your domain."
        scriptTail="Your Stripe. Your customers."
        lead="No marketplace cut. No platform lock-in. No 30% commissions. Plug your own domain into our DNS and your own Stripe Connect Express account into our destination-charge pipeline. We're the rails — you're the brand."
      >
        <Card className="p-8 h-full bg-zinc-900 border-zinc-800 text-background">
          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <DarkFeat icon={Globe} title="Bring your own domain">Add a CNAME record at your registrar — we handle SSL, routing, and provisioning automatically. No subdomain rental, no transfer required.</DarkFeat>
            <DarkFeat icon={ShieldCheck} title="Bring your own Stripe">Stripe Connect Express onboarding in minutes. Funds route straight to your bank account on Stripe's standard 2-day payout — Ordrs never holds your money.</DarkFeat>
            <DarkFeat icon={Receipt} title="Two fee models">Pass a flat $1.50 convenience fee to customers (net-zero overhead), or absorb 1% yourself for a cleaner checkout experience. Switch anytime in Settings.</DarkFeat>
            <DarkFeat icon={Database} title="Own your customer data">Every order or booking builds your CRM. Export anytime. No platform holds your list hostage.</DarkFeat>
          </div>
          <div className="bg-black/40 border border-zinc-800 rounded-lg p-5 font-mono text-xs">
            <div className="text-background/50">// destination charge — customer pays $1.50</div>
            <div>application_fee_amount: <span className="text-primary">150</span></div>
            <div>transfer_data.destination: <span className="text-primary">acct_yourstripe</span></div>
            <div className="text-background/50">// funds in your account in 2 business days</div>
          </div>
        </Card>
      </Section>

      {/* 07 — FULFILLMENT & SCHEDULING */}
      <Section
        id="s07" idx="07" kicker="fulfillment & scheduling"
        title="Bulletproof order and"
        scriptTail="booking routing."
        lead="An order or appointment is only as good as your ability to act on it. ordrs links your web and phone channels into a single queue or calendar — eliminating manual data entry, missed orders, double-bookings, and tablet sprawl. Works for any business that prepares orders or schedules time."
      >
        <Card className="p-8 h-full">
          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <Feat icon={Printer} title="Browser-native printing">Click the receipt icon on any order to print to any connected printer — thermal, inkjet, USB, or Bluetooth. No SDK, no extra software.</Feat>
            <Feat icon={CheckCircle2} title="Strict idempotency">ID-based idempotency keys guarantee no ticket prints twice and no appointment double-books, even during webhook retries.</Feat>
            <Feat icon={Zap} title="Webhook-driven pipeline">checkout.session.completed → mark paid → update queue or calendar → print ticket or confirm slot. One atomic flow, no manual steps.</Feat>
            <Feat icon={Clock} title="Checkout & booking messaging">Set a custom message shown above checkout — "Ready in 15–20 min," "We'll text when it's ready," "See you at 2:15" — plus your tax rate and fee model, all from the Checkout tab.</Feat>
          </div>
          <div className="bg-foreground text-background rounded-lg p-5 font-mono text-xs space-y-1.5">
            <div className="text-background/50">// order / booking lifecycle</div>
            <div>stripe <span className="text-primary">checkout.session.completed</span></div>
            <div>→ status: <span className="text-primary">paid</span></div>
            <div>→ queue / calendar: <span className="text-primary">Pending</span></div>
            <div>→ receipt printed or slot confirmed: <span className="text-primary">✓ (idempotent)</span></div>
          </div>
        </Card>
      </Section>

      {/* 08 — ENTERPRISE COMPLIANCE */}
      <Section
        id="s08" idx="08" kicker="enterprise compliance" dark
        title="Bank-level"
        scriptTail="security & sovereignty."
        lead="Your business data belongs to you, period. Built on locked-down, corporate-grade database architecture with row-level tenant isolation, PCI-friendly payment handling, and a full audit trail — so your system stays sound even at peak volume."
      >
        <Card className="p-8 h-full bg-zinc-900 border-zinc-800 text-background">
          <div className="grid sm:grid-cols-2 gap-6">
            <DarkFeat icon={Lock} title="Strict tenant isolation">Every record is keyed by tenant_id. RLS-ready schema with no cross-account data bleed.</DarkFeat>
            <DarkFeat icon={ShieldCheck} title="PCI-friendly by design">Card data never touches our servers — Stripe hosts the checkout. We only see the completed session confirmation.</DarkFeat>
            <DarkFeat icon={Database} title="Audit-grade tracking">Every catalog or service change, order or booking modification, and printed ticket is logged with timestamp and version. CCPA deletion available per phone number from the Call Logs tab.</DarkFeat>
            <DarkFeat icon={Building2} title="Multi-location ready">Enterprise tier scales per-location at $99/mo. Centralized dashboard, distributed queues and calendars.</DarkFeat>
          </div>
        </Card>
      </Section>

      {/* 09 — IMMEDIATE DEPLOYMENT */}
      <Section
        id="s09" idx="09" kicker="immediate deployment"
        title="From paste to public"
        scriptTail="in five minutes."
        lead="No infrastructure to provision. No subdomain to claim from us. No onboarding call required. Free preview — one included refinement, $5 for 10 more — then a one-time $899 setup fee (or $150/mo × 6) to flip checkout live."
      >
        <Card className="p-8 h-full">
          <div className="space-y-5">
            {[
              ["1", "Paste your catalog or services", "Text, PDF, image, or existing website URL. 30 seconds."],
              ["2", "Pick a style", "4 archetypes or describe your own aesthetic. 20 seconds."],
              ["3", "Click build", "AI generates your storefront. ~3 minutes."],
              ["4", "Iterate", "Type changes in plain English. Free refinement included; $5 for 10 more."],
              ["5", "Go live", "Stripe Connect → pick plan → $899 setup fee → checkout flips on. ~1 minute."],
            ].map(([num, t, d]) => (
              <div key={num} className="flex gap-4 items-start">
                <div className="shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground font-bold grid place-items-center font-mono">
                  {num}
                </div>
                <div>
                  <div className="font-bold">{t}</div>
                  <div className="text-sm text-muted-foreground">{d}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </Section>

      {/* 10 — AI BUSINESS ADVISOR */}
      <Section
        id="s10" idx="10" kicker="ai business advisor"
        title="A built-in AI that watches your"
        scriptTail="back while you run the front."
        lead="Every dashboard comes with an AI advisor that quietly observes your business and offers short, contextual tips. No setup. No configuration. It just works — right in the footer of your dashboard."
      >
        <div className="grid md:grid-cols-2 gap-4">
          <Feat icon={Brain} title="Page-aware suggestions">
            It knows which screen you are on — orders, appointments, products, services, fulfillment, or builder — and tailors every tip to what you are doing right now. Switch from Dashboard to Kitchen to Build and the advice changes with you.
          </Feat>
          <Feat icon={Database} title="Merchant-specific context">
            It pulls live data from your storefront — your business name, order volume, top sellers, appointment counts, catalog size, and more. Every suggestion is grounded in what is actually happening in your business today.
          </Feat>
          <Feat icon={Sparkles} title="Ambient, not intrusive">
            One-line tips rotate in a subtle bottom bar every few seconds. Click to pause. No popups, no interruptions, no clicking around. Just helpful guidance that stays out of your way.
          </Feat>
          <Feat icon={ShieldCheck} title="Private by design">
            Only visible screen content is used. No customer PII is sent to the model. The AI is instructed not to retain or log any data. Your business information stays yours.
          </Feat>
          <Feat icon={Building2} title="Works for any merchant">
            Product businesses (florists, bakeries, retailers) and service providers (salons, spas, appointments) alike. The advisor adapts automatically based on what it sees on your dashboard.
          </Feat>
          <Feat icon={Zap} title="Included with every plan">
            No extra cost. No add-on. No toggle to flip. Every Ordrs subscription includes the AI advisor — it activates automatically when you open your dashboard.
          </Feat>
        </div>
      </Section>

      {/* CTA */}
      <section data-story-section className="solutions-panel solutions-panel--cta">
        <div className="solutions-panel-inner text-center" data-story-inner>
          <motion.div className="solutions-hero-card" {...fade}>
          <p className="overline mb-4">ready when you are</p>
          <h2 className="text-5xl lg:text-6xl mb-6">
            <span className="font-script text-primary text-6xl lg:text-7xl font-normal block leading-[0.7] mb-3">
              Stop renting
            </span>
            your customers from third-party platforms.
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Free preview. No card. Bring your own domain. Keep 100% of your data.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/">
              <Button size="lg" className="h-12 px-8 text-base font-bold" data-testid="solutions-cta-build">
                Build my storefront <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="h-12 px-8 text-base font-bold" data-testid="solutions-cta-pricing">
                See pricing
              </Button>
            </Link>
          </div>
          </motion.div>
        </div>
      </section>
      </StoryScroll>

      <SiteFooter />
    </div>
  );
}
